import { v4 as uuidv4 } from 'uuid';
import { ChartId } from '@blisspointmedia/bpm-types/dist/dashboarding';

// A chart the agent builds from nothing has no saved `chart:<uuid>` id until the user saves it — but
// its working copy, URL, and focus context all need a stable handle so multi-turn editing works before
// the first save. A DRAFT handle fills that gap: the same shape as a ChartId with a `draft:` prefix,
// minted client-side at the first emit and migrated to the real `chart:<uuid>` on Save (see
// SingleChartView's Save chart exit). A ChartHandle is whichever one a view/route is pointed at.
export type DraftId = `draft:${string}`;
export type ChartHandle = ChartId | DraftId;

export const isDraftId = (handle: string | null | undefined): handle is DraftId =>
  typeof handle === 'string' && handle.startsWith('draft:');

export const newDraftId = (): DraftId => `draft:${uuidv4()}`;

/**
 * The preview-only ChartId for an unsaved draft, derived from its own handle.
 *
 * A brand-new chart the agent emits carries NO id — chart identity is assigned by the database on
 * save, never invented by a client (and emphatically never by a language model: LLM-authored uuids
 * repeat, which is how one company's chart row ended up overwriting another's). But the preview path
 * — working copy → useChartData → the renderers — is typed for SAVED charts, so an unsaved config
 * still needs something in `id` to render.
 *
 * This supplies it from the draft's own uuid, so the two are visibly the same chart. It is
 * preview-only and never persisted: `performSaveAsNew` deletes `id` before POSTing, and the
 * dashboard-append path sends the agent's id-less config to /saveChart untouched. The uuid is
 * crypto-random, so even a leak could not collide.
 */
export const previewChartIdFor = (draftId: DraftId): ChartId =>
  `chart:${draftId.slice('draft:'.length)}`;
